import { Component, EventEmitter, inject, Input, OnInit, Output } from "@angular/core";
import { FormControl, UntypedFormBuilder, UntypedFormGroup, ValidatorFn, Validators } from "@angular/forms";

import { Environment, ENVIRONMENT_TOKEN } from "@intocare/environments";

@Component({
	selector: "ic-organisation-form",
	templateUrl: "./organisation-form.component.html",
	styleUrls: ["./organisation-form.component.scss"],
	standalone: false,
})
export class OrganisationFormComponent implements OnInit {
	private formBuilder = inject(UntypedFormBuilder);
	private environment = inject<Environment>(ENVIRONMENT_TOKEN);

	@Input() loading: boolean;
	@Input() errorLabel: string;
	@Input() errorForInput: string;

	@Output() selectOrganisation = new EventEmitter();

	form: UntypedFormGroup;

	constructor() {
		const noSpecialCharactersPattern = /^[a-zA-Z0-9.]+$/;
		const validHomeOrHqPattern = this.environment.platform === "home" ? /^[a-z0-9]+\.[a-z0-9]+$/ : /^[a-z0-9]+$/;

		this.form = this.formBuilder.group({
			organisation: [
				"",
				[
					Validators.required,
					this.validateRegex("invalid_organisation_trigram_with_special_characters", noSpecialCharactersPattern),
					this.validateRegex("invalid_organisation_trigram", validHomeOrHqPattern),
				],
			],
		});
	}

	@Input()
	set organisation(value: string) {
		if (!value) {
			return;
		}

		this.form.patchValue({ organisation: value });
	}

	get shouldShowError() {
		const control = this.form.get("organisation");

		if (control.touched) {
			if (control.invalid) {
				return true;
			}

			if (this.errorLabel && control.value === this.errorForInput) {
				return true;
			}
		}

		return false;
	}

	get errorLabelForInput() {
		const control = this.form.get("organisation");

		// Required error
		if (control.hasError("required")) {
			return "labels.error.required";
		}

		if (control.hasError("invalid_organisation_trigram_with_special_characters")) {
			return "auth.errors.invalid_organisation_trigram_with_special_characters";
		}

		if (control.hasError("invalid_organisation_trigram")) {
			return "auth.errors.invalid_organisation_trigram";
		}

		// If input is checked and proven to give an error
		if (control.value === this.errorForInput) {
			return this.errorLabel;
		}

		return "";
	}

	validateRegex(customError: string, pattern: RegExp): ValidatorFn {
		return (control: FormControl) => {
			if (control.value && !pattern.test(control.value)) {
				return { [customError]: true };
			}

			return null;
		};
	}

	submit() {
		this.selectOrganisation.emit(this.form.get("organisation").value);
	}

	ngOnInit() {
		const organisation = window.localStorage.getItem("currentOrganisation");
		if (organisation) {
			this.form.patchValue({ organisation });
			this.form.get("organisation").markAsTouched();
		}
	}
}
